import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';

import { getWeekDays, getWeekRange, showMsg } from '../../services/library';
import InventoryService from '../../services/inventoryService';
import { TIME_HOUR_LIST } from '../../constants/arrayListConst';
import DealerService from '../../services/dealerService';

const REQ_COMPARISON = 'dealerComparison/';

export const fetchDealerComparisonReq = createAsyncThunk(
  `${REQ_COMPARISON}fetchDealerComparisonReq`,
  async (data, { rejectWithValue }) => {
    try {
      const response = await InventoryService.getDealerComparison(data);
      if (response?.status) {
        return response.data[0];
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchSettingsReq = createAsyncThunk(
  `${REQ_COMPARISON}fetchSettingsReq`,
  async (data, { rejectWithValue }) => {
    try {
      const response = await DealerService.invLowSettings(data);
      if (response?.status) {
        return response.data[0];
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

const TIME_LIST = TIME_HOUR_LIST('Now');
const defaultTime = TIME_LIST[0];
const initialState = {
  activeTab: 'general',
  stateUpdate: false,
  isEditing: false,
  dealerProfileData: {},
  profileLoaded: false,
  dealerProfileTeam: [],
  teamLoaded: false,
  paChartData: [],
  ddChartData: [],
  changeInfo: [],
  paChartDataLoaded: false,
  ddChartDataLoaded: false,
  user_paexposure: false,
  user_pafocus: false,
  is_dealer_mavis_color: false,
  // is_mavis3: false,
  is_dealer_inhouse_video: false,
  is_dealer_inhouse_bgremoval: false,
  is_active: false,
  is_feature: false,
  is_autopublish: false,
  is_window_sticker_publish: false,
  show_window_sticker_publish: false,
  is_autopublishvds: false,
  is_gallery_upload: false,
  is_customWarrantyForm: false,
  is_prelimremovebg: false,
  is_ext360removebg: false,
  is_qrwithlabel: false,
  is_ytintegrated: false,
  is_promobanner: false,
  is_promobillboard: false,
  is_matchVDS: true,
  is_lightVDS: false,
  is_pbsVDS: false,
  is_import_download: true,
  is_share_vehicle: false,
  is_forceLogout: false,
  is_export_upload: false,
  is_user_allow_iphoneapp: false,
  dealer_exclude_ims_from_with_photos: false,
  is_trainingmode: false,
  // is_encPhotonURL: false,
  dealer_allow_vdsclone: false,
  user_debug: false,
  teamListToggle: false,
  paStartDate: moment().toDate(),
  paSelectedDays: getWeekDays(getWeekRange(moment().toDate()).from),
  ddStartDate: moment().toDate(),
  ddSelectedDays: getWeekDays(getWeekRange(moment().toDate()).from),
  logoutHour: defaultTime,
  recipients: [],
  body_types: [],
  body_typesLoading: true,
  isAssignBodyTypes: false,
  // preStockImageVersion: new Date().getTime(),
  // applyps: PS_LIST[0], // Replace with the actual PS_LIST[0] value if it's a variable
  dealer_qbinvoicegroup_id: '',
  isCancellationModal: false,
  settingRadio: 1,
  // Low Photos
  lownew: 0,
  lowused: 0,
  lowdataLoaded: false,
  // VDS group
  copyVDSGroups: [],
  copyVDSGroupsLoaded: false,
  copyVDSModalToggle: false,
  paUploadGroups: [],
  paUploadGroupsLoaded: false,
  paUploadGroupModalToggle: false,
  is_window_sticker_dashboard_allowed: false,
  isUserAllowedUploadNotification: false
};

const dealerProfileSlice = createSlice({
  name: 'dealerProfile',
  initialState,
  reducers: {
    handleDealerLowNew: {
      reducer(state, action) {
        state.lownew = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    handleDealerLowUsed: {
      reducer(state, action) {
        state.lowused = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    handleDealerLoaded: {
      reducer(state, action) {
        state.lowdataLoaded = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    // setIsMavis3: {
    //   reducer(state, action) {
    //     state.is_mavis3 = action.payload;
    //   },
    //   prepare(payload) {
    //     return { payload };
    //   }
    // },
    setActiveTab: {
      reducer(state, action) {
        state.activeTab = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setStateUpdate: {
      reducer(state, action) {
        state.stateUpdate = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsEditing: {
      reducer(state, action) {
        state.isEditing = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setDealerProfileData: {
      reducer(state, action) {
        state.dealerProfileData = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setProfileLoaded: {
      reducer(state, action) {
        state.profileLoaded = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setDealerProfileTeam: {
      reducer(state, action) {
        state.dealerProfileTeam = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setTeamLoaded: {
      reducer(state, action) {
        state.teamLoaded = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setPaChartData: {
      reducer(state, action) {
        state.paChartData = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setDdChartData: {
      reducer(state, action) {
        state.ddChartData = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setChangeInfo: {
      reducer(state, action) {
        state.changeInfo = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setPaChartDataLoaded: {
      reducer(state, action) {
        state.paChartDataLoaded = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setDdChartDataLoaded: {
      reducer(state, action) {
        state.ddChartDataLoaded = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setUserPaExposure: {
      reducer(state, action) {
        state.user_paexposure = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setUserPaFocus: {
      reducer(state, action) {
        state.user_pafocus = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsDealerMavisColor: {
      reducer(state, action) {
        state.is_dealer_mavis_color = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsDealerInhouseVideo: {
      reducer(state, action) {
        state.is_dealer_inhouse_video = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsDealerInhouseBgRemoval: {
      reducer(state, action) {
        state.is_dealer_inhouse_bgremoval = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsActive: {
      reducer(state, action) {
        state.is_active = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsFeature: {
      reducer(state, action) {
        state.is_feature = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsAutoPublish: {
      reducer(state, action) {
        state.is_autopublish = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsWindowStickerPublish: {
      reducer(state, action) {
        state.is_window_sticker_publish = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setShowWindowStickerPublish: {
      reducer(state, action) {
        state.show_window_sticker_publish = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsAutoPublishVds: {
      reducer(state, action) {
        state.is_autopublishvds = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsGalleryUpload: {
      reducer(state, action) {
        state.is_gallery_upload = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsCustomWarrantyForm: {
      reducer(state, action) {
        state.is_customWarrantyForm = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsPrelimRemoveBg: {
      reducer(state, action) {
        state.is_prelimremovebg = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsExt360RemoveBg: {
      reducer(state, action) {
        state.is_ext360removebg = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsQrwithlabel: {
      reducer(state, action) {
        state.is_qrwithlabel = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },

    setIsYTIntegrated: {
      reducer(state, action) {
        state.is_ytintegrated = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIspromobanner: {
      reducer(state, action) {
        state.is_promobanner = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIspromobillboard: {
      reducer(state, action) {
        state.is_promobillboard = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setDealerAllowVdsClone: {
      reducer(state, action) {
        state.dealer_allow_vdsclone = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setUserDebug: {
      reducer(state, action) {
        state.user_debug = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setTeamListToggle: {
      reducer(state, action) {
        state.teamListToggle = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setPaStartDate: {
      reducer(state, action) {
        state.paStartDate = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setSelectedDays: {
      reducer(state, action) {
        state.paSelectedDays = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setDdStartDate: {
      reducer(state, action) {
        state.ddStartDate = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setDdSelectedDays: {
      reducer(state, action) {
        state.ddSelectedDays = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setLogoutHour: {
      reducer(state, action) {
        state.logoutHour = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setRecipients: {
      reducer(state, action) {
        state.recipients = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setBodyTypes: {
      reducer(state, action) {
        state.body_types = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setBodyTypesLoading: {
      reducer(state, action) {
        state.body_typesLoading = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsAssignBodyTypes: {
      reducer(state, action) {
        state.isAssignBodyTypes = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    // setPreStockImageVersion: {
    //   reducer(state, action) {
    //     state.preStockImageVersion = action.payload;
    //   },
    //   prepare(payload) {
    //     return { payload };
    //   }
    // },
    // setApplyps: {
    //   reducer(state, action) {
    //     state.applyps = action.payload;
    //   },
    //   prepare(payload) {
    //     return { payload };
    //   }
    // },
    setDealerQbinvoicegroupId: {
      reducer(state, action) {
        state.dealer_qbinvoicegroup_id = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsCancellationModal: {
      reducer(state, action) {
        state.isCancellationModal = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setSettingRadio: {
      reducer(state, action) {
        state.settingRadio = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsMatchVDS: {
      reducer(state, action) {
        state.is_matchVDS = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsLightVDS: {
      reducer(state, action) {
        state.is_lightVDS = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsPbsVDS: {
      reducer(state, action) {
        state.is_pbsVDS = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsImportDownload: {
      reducer(state, action) {
        state.is_import_download = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsShareVehicle: {
      reducer(state, action) {
        state.is_share_vehicle = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsForceLogout: {
      reducer(state, action) {
        state.is_forceLogout = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsExportUpload: {
      reducer(state, action) {
        state.is_export_upload = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setIsAllowIphoneApp: {
      reducer(state, action) {
        state.is_user_allow_iphoneapp = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setExcludeImsFromWithPhotos: {
      reducer(state, action) {
        state.dealer_exclude_ims_from_with_photos = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setTrainingMode: {
      reducer(state, action) {
        state.is_trainingmode = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setUserUploadNotification: {
      reducer(state, action) {
        state.isUserAllowedUploadNotification = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    // VDS Group
    setCopyVDSGroups: {
      reducer(state, action) {
        state.copyVDSGroups = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setCopyVDSGroupsLoaded: {
      reducer(state, action) {
        state.copyVDSGroupsLoaded = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setCopyVDSModalToggle: {
      reducer(state, action) {
        state.copyVDSModalToggle = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setPaUploadGroups: {
      reducer(state, action) {
        state.paUploadGroups = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setPaUploadGroupsLoaded: {
      reducer(state, action) {
        state.paUploadGroupsLoaded = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setPaUploadGroupModalToggle: {
      reducer(state, action) {
        state.paUploadGroupModalToggle = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setAllowWindowStickerDashboard: {
      reducer(state, action) {
        state.is_window_sticker_dashboard_allowed = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    }
    // setIsEncPhotonURL: {
    //   reducer(state, action) {
    //     state.is_encPhotonURL = action.payload;
    //   },
    //   prepare(payload) {
    //     return { payload };
    //   }
    // }
  },
  extraReducers: builder => {
    builder
      // #region fetchSettingsReq
      .addCase(fetchSettingsReq.pending, state => {
        state.lownew = 0;
        state.lowused = 0;
        state.lowdataLoaded = false;
      })
      .addCase(fetchSettingsReq.fulfilled, (state, action) => {
        const { dealer_lownew, dealer_lowused } = action.payload;
        state.lownew = dealer_lownew;
        state.lowused = dealer_lowused;
        state.lowdataLoaded = true;
      })
      .addCase(fetchSettingsReq.rejected, state => {
        state.lownew = 0;
        state.lowused = 0;
        state.isDataLoading = true;
      });
    // #endregion
  }
});

export const {
  // VDS Group
  setCopyVDSGroups,
  setCopyVDSGroupsLoaded,
  setCopyVDSModalToggle,

  setPaUploadGroups,
  setPaUploadGroupsLoaded,
  setPaUploadGroupModalToggle,

  // setIsMavis3,
  setActiveTab,
  setStateUpdate,
  setIsEditing,
  setDealerProfileData,
  setProfileLoaded,
  setDealerProfileTeam,
  setTeamLoaded,
  setPaChartData,
  setDdChartData,
  setChangeInfo,
  setPaChartDataLoaded,
  setDdChartDataLoaded,
  setUserPaExposure,
  setUserPaFocus,
  setIsDealerMavisColor,
  setIsDealerInhouseVideo,
  setIsDealerInhouseBgRemoval,
  setIsActive,
  setIsFeature,
  setIsAutoPublish,
  setIsWindowStickerPublish,
  setShowWindowStickerPublish,
  setIsAutoPublishVds,
  setIsGalleryUpload,
  setIsCustomWarrantyForm,
  setIsPrelimRemoveBg,
  setIsExt360RemoveBg,
  setIsQrwithlabel,
  setIsYTIntegrated,
  setIspromobanner,
  setIspromobillboard,
  setDealerAllowVdsClone,
  setUserDebug,
  setTeamListToggle,
  setPaStartDate,
  setSelectedDays,
  setDdStartDate,
  setDdSelectedDays,
  setLogoutHour,
  setRecipients,
  setBodyTypes,
  setBodyTypesLoading,
  setIsAssignBodyTypes,
  // setPreStockImageVersion,
  // setApplyps,
  setDealerQbinvoicegroupId,
  setIsCancellationModal,
  setSettingRadio,
  setIsMatchVDS,
  setIsLightVDS,
  setIsPbsVDS,
  setIsImportDownload,
  setIsForceLogout,
  // setIsEncPhotonURL,
  handleDealerLoaded,
  handleDealerLowNew,
  handleDealerLowUsed,
  setIsExportUpload,
  setIsShareVehicle,
  setIsAllowIphoneApp,
  setExcludeImsFromWithPhotos,
  setTrainingMode,
  setAllowWindowStickerDashboard,
  setUserUploadNotification
} = dealerProfileSlice.actions;
export default dealerProfileSlice.reducer;
